import type { SessionProviderPlatform } from "./use-session-provider-platform";
import type { SessionProviderStateBundle } from "./use-session-provider-state-bundle";
import type { SessionProviderLiveWiring } from "./use-session-provider-live-wiring";
import { useSessionAccessModals } from "./use-session-access-modals";
import { useSessionCollabMerges } from "./use-session-collab-merges";
import { useSessionOverviewState } from "./use-session-overview-state";

export interface SessionProviderOverviewWiring {
  accessModals: ReturnType<typeof useSessionAccessModals>;
  overview: ReturnType<typeof useSessionOverviewState>;
  collabMerges: ReturnType<typeof useSessionCollabMerges>;
}

export function useSessionProviderOverviewWiring(input: {
  platform: SessionProviderPlatform;
  state: SessionProviderStateBundle;
  liveWiring: SessionProviderLiveWiring;
}): SessionProviderOverviewWiring {
  const { liveWiring, platform, state } = input;
  const accessModals = useSessionAccessModals({
    api: platform.api,
    showMessageModal: platform.showMessageModal,
  });
  const overview = useSessionOverviewState({
    haltStatusDebugLog: platform.haltStatusDebugLog,
    showInsufficientTokensModal: accessModals.showInsufficientTokensModal,
  });
  const collabMerges = useSessionCollabMerges({
    collabsByProductRef: state.refs.collabsByProductRef,
    chatMessageIdsRef: state.refs.chatMessageIdsRef,
    ownMessageIdsRef: liveWiring.readTracking.ownMessageIdsRef,
    setCollabs: state.conversation.setCollabs,
    setCollabsByProduct: state.conversation.setCollabsByProduct,
    setChatMessages: state.conversation.setChatMessages,
    setChatMessageIds: state.conversation.setChatMessageIds,
    setNewMessageIds: state.interaction.setNewMessageIds,
    updateProductsSeen: liveWiring.readTracking.updateProductsSeen,
    updateProductCollabsSinceTimestamp:
      liveWiring.timestamps.updateProductCollabsSinceTimestamp,
    tagOwnCollabsAndAdvanceRead:
      liveWiring.readTracking.tagOwnCollabsAndAdvanceRead,
    flagCollabArrivalsAndAncestors:
      liveWiring.liveSignals.flagCollabArrivalsAndAncestors,
    pushMemberActivitiesFromCollabs:
      liveWiring.liveSignals.pushMemberActivitiesFromCollabs,
  });

  return {
    accessModals,
    overview,
    collabMerges,
  };
}
